import classnames from 'classnames';
import React from 'react';

import cs from 'app/components/Sidebar/styles.module.scss';

export interface SidebarActionBarProps {
  /** What the actions will apply to. */
  context?: React.ReactNode;
  /** Trailing control on the context row, for clearing that selection. */
  contextAction?: React.ReactNode;
  className?: string;
}

/**
 * Bar pinned to the bottom of a panel, holding the actions that finish what the
 * user is doing to the current selection. The first child is the primary action
 * and fills the remaining width.
 */
export const SidebarActionBar: React.FC<React.PropsWithChildren<SidebarActionBarProps>> = ({
  context,
  contextAction,
  className,
  children,
}) => (
  <div className={classnames(cs.actionBar, className)}>
    {(context || contextAction) && (
      <div className={cs.actionBarContext}>
        {context}
        {contextAction && <span className={cs.actionBarContextAction}>{contextAction}</span>}
      </div>
    )}
    <div className={cs.actionBarButtons}>{children}</div>
  </div>
);

/** Bold, truncating label naming the action bar's current target. */
export const SidebarActionBarLabel: React.FC<React.PropsWithChildren<{className?: string}>> = ({
  className,
  children,
}) => <span className={classnames(cs.actionBarContextLabel, className)}>{children}</span>;

export interface SidebarActionBarHintProps {
  /** What the user needs to do before the action bar has a selection. */
  children: React.ReactNode;
  /** Keyboard shortcuts available in this panel. */
  shortcuts?: React.ReactNode;
  className?: string;
}

/** The action bar with nothing selected yet. */
export const SidebarActionBarHint: React.FC<SidebarActionBarHintProps> = ({
  children,
  shortcuts,
  className,
}) => (
  <div className={classnames(cs.actionBarHint, className)}>
    <div>{children}</div>
    {shortcuts && <div className={cs.actionBarShortcuts}>{shortcuts}</div>}
  </div>
);
